/**
 * Player theme registry: one CSS-variable palette per entry.
 *
 * `tottalo` is the legacy palette — PNG icons, hand-tuned CSS, never changes —
 * kept only for admin-assigned legacy players; it's no longer anyone's default
 * and never appears in the player-facing switcher (see PLAYER_SELECTABLE_THEMES).
 *
 * `claude` is the light palette (warm paper, terracotta accent). `green` and
 * `golden` are the two dark palettes drawn from the approved design mockups —
 * deep green/charcoal shells with, respectively, an emerald and a champagne
 * accent. `blue` is the navy palette of the modern-layout mockups (blue chips,
 * yellow buttons). All three render dark content surfaces, which the rest of the app supports
 * through the `--theme-*` tokens in styles/tokens.css.
 */
export const PLAYER_THEME_IDS = [
  'tottalo',
  'claude',
  'green',
  'golden',
  'blue',
] as const;

export type PlayerThemeId = (typeof PLAYER_THEME_IDS)[number];

export const THEMES: { name: string; value: PlayerThemeId }[] = [
  { name: 'Tottalo', value: 'tottalo' },
  { name: 'Claude', value: 'claude' },
  { name: 'Green', value: 'green' },
  { name: 'Golden', value: 'golden' },
  { name: 'Blue', value: 'blue' },
];

// Golden is the brand look (icon, link preview): the login screen and anyone
// without a theme of their own get it. It was 'claude', whose terracotta
// accent made the login screen orange.
export const DEFAULT_THEME: PlayerThemeId = 'golden';

/** Themes whose content surfaces are dark, so anything painted on them needs
 *  light ink. Kept here (not in CSS) because a few components pick artwork or
 *  icon tone in JS. */
export const DARK_PLAYER_THEMES: readonly PlayerThemeId[] = [
  'green',
  'golden',
  'blue',
];

export function isDarkPlayerTheme(theme: PlayerThemeId): boolean {
  return DARK_PLAYER_THEMES.includes(theme);
}

/**
 * Themes a player may pick for themselves in the in-app switcher. "tottalo" is
 * excluded on purpose — it's the legacy default and only an admin can assign it,
 * from the admin panel.
 */
export const PLAYER_SELECTABLE_THEMES = THEMES.filter(
  (t) => t.value !== 'tottalo'
);
